import { useCallback, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

/**
 * 揽收运单号扫描灰度开关。
 *
 *   trackingScanEnabled(hub) = wholeSwitch || hubIds.contains(hub)
 *
 * 单一源：后端 pickup common_config `pickup.tracking.scan.gray.switch`，经 api-server GraphQL
 * `getPickupTrackingScanGraySwitch` 暴露。司机端据此决定是否展示运单号扫描模块，
 * 运营台据此决定是否展示揽收进度的「预约单上网情况」与「未上网预约单数」——两端读同一份名单。
 *
 * 与 usePickupSiteV2Gray 是两份独立名单：2.0 开关上已挂审核池 / 新状态机 / AB / 合并等一批功能，
 * 共用会让两个功能无法独立放量与回滚。
 *
 * 调整灰度改后端 common_config 即可（JSON {wholeSwitch,hubIds}，缓存 60s 生效）。
 */
export interface PickupTrackingScanGraySwitch {
  wholeSwitch: boolean;
  hubIds: string[];
}

export const GET_PICKUP_TRACKING_SCAN_GRAY_SWITCH = gql`
  query GetPickupTrackingScanGraySwitch {
    getPickupTrackingScanGraySwitch {
      wholeSwitch
      hubIds
    }
  }
`;

interface PickupTrackingScanGrayResponse {
  getPickupTrackingScanGraySwitch?: {
    wholeSwitch: boolean;
    // 后端 hubIds 为 Long，GraphQL 序列化为字符串
    hubIds: Array<string | number> | null;
  } | null;
}

export function usePickupTrackingScanGray() {
  const { data, loading } = useQuery<PickupTrackingScanGrayResponse>(
    GET_PICKUP_TRACKING_SCAN_GRAY_SWITCH,
    { fetchPolicy: 'cache-first' },
  );
  const cfg = data?.getPickupTrackingScanGraySwitch;
  const wholeSwitch = cfg?.wholeSwitch ?? false;
  const hubIds = useMemo(() => (cfg?.hubIds ?? []).map(String), [cfg]);

  const trackingScanEnabled = useCallback(
    (hubId?: string | number | null): boolean => {
      // 失败关闭：加载中/无 hub 上下文一律未命中，避免"任一 hub 灰度即全量"
      if (hubId === null || hubId === undefined || hubId === '') return false;
      return wholeSwitch || hubIds.includes(String(hubId));
    },
    [wholeSwitch, hubIds],
  );

  return { wholeSwitch, hubIds, trackingScanEnabled, loading };
}
